import React, { useState, useEffect } from 'react';
import GeofenceList from './Splits/GeofenceList';
import GeofenceMap from './Splits/GeofenceMap';
import GeofenceModal from './Splits/GeofenceModal';
import ImportGeofenceModal from './Splits/ImportGeofenceModal';
import { fetchGeofences, createGeofence, updateGeofence, deleteGeofence } from '../../services/api/geofenceApi';
import './Splits/AdminSplits.css';

const AdminGeofences = () => {
    const [geofences, setGeofences] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedGeofence, setSelectedGeofence] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    useEffect(() => {
        loadGeofences();
    }, []);

    const loadGeofences = async () => {
        setLoading(true);
        try {
            const data = await fetchGeofences();
            console.log("AdminGeofences: Loaded geofences into state:", data);
            setGeofences(data);
        } catch (error) {
            console.error('Failed to load geofences:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleGeofenceClick = (gf) => {
        setSelectedGeofence(gf);
    };

    const handleAddClick = () => {
        setSelectedGeofence(null); // Clear selection for new item
        setIsEditModalOpen(true);
    };

    const handleImportClick = () => {
        setIsImportModalOpen(true);
    };

    const handleSaveGeofence = async (geofenceData) => {
        try {
            if (geofenceData.id) {
                // Update
                await updateGeofence(geofenceData);
            } else {
                // Create
                // Ensure default area if not provided (e.g. user clicked map)
                // If no area, maybe default to 0,0 or ask user to click map?
                // For now, let api handle or error
                await createGeofence(geofenceData);
            }
            setIsEditModalOpen(false);
            loadGeofences();
        } catch (error) {
            alert('Failed to save geofence: ' + error.message);
        }
    };

    const handleDeleteGeofence = async (id) => {
        if (!window.confirm('Are you sure you want to delete this geofence?')) return;
        try {
            await deleteGeofence(id);
            if (selectedGeofence?.id === id) setSelectedGeofence(null);
            loadGeofences();
        } catch (error) {
            alert('Failed to delete: ' + error.message);
        }
    };

    const handleImportStage = async (stage) => {
        // Here we would implement the logic to create geofences from stage data
        // For now, we'll create a placeholder Start and Meta geofence for this stage
        try {
            const startGeofence = {
                name: `START - ${stage.name}`,
                description: `Start for stage ${stage.name}`,
                area: 'CIRCLE (52.0 21.0, 50)', // Placeholder coord
                attributes: {
                    pointType: 'START',
                    stageId: stage.id,
                    orderNo: 0
                }
            };

            const metaGeofence = {
                name: `META - ${stage.name}`,
                description: `Meta for stage ${stage.name}`,
                area: 'CIRCLE (52.1 21.1, 50)', // Placeholder coord
                attributes: {
                    pointType: 'META',
                    stageId: stage.id,
                    orderNo: 999
                }
            };

            await createGeofence(startGeofence);
            await createGeofence(metaGeofence);

            setIsImportModalOpen(false);
            loadGeofences();
            alert('Imported placeholder geofences for stage. Please adjust positions on map.');
        } catch (error) {
            alert('Import failed: ' + error.message);
        }
    };

    const handleMapClick = (latlng) => {
        // If in "New Mode" or "Edit Mode", update the area?
        // For now, basic interaction: Log
        console.log("Map clicked at", latlng);
    };

    const handleEditClick = (gf) => {
        setSelectedGeofence(gf);
        setIsEditModalOpen(true);
    };

    const handleGeofenceCreate = (area) => {
        // Create a new temporary geofence object with the drawn area
        const newGeofence = {
            name: '',
            description: '',
            area: area,
            attributes: {
                pointType: 'SPLIT',
                stageId: '',
                orderNo: ''
            }
        };
        setSelectedGeofence(newGeofence);
        setIsEditModalOpen(true);
    };

    return (
        <div className="admin-splits-container">
            {/* Sidebar */}
            <div className="splits-sidebar" style={{ width: sidebarCollapsed ? '0' : '350px' }}>
                <div className="splits-sidebar-header">
                    <h2 className="splits-sidebar-title">Geofences</h2>
                    <div className="splits-actions">
                        <button className="splits-action-btn" title="Add Geofence" onClick={handleAddClick}>
                            <span className="material-icons-outlined">add</span>
                        </button>
                        <button className="splits-action-btn" title="Import from Stage" onClick={handleImportClick}>
                            <span className="material-icons-outlined">upload_file</span>
                        </button>
                        <button className="splits-action-btn" title="Refresh" onClick={loadGeofences}>
                            <span className="material-icons-outlined">refresh</span>
                        </button>
                    </div>
                </div>

                <GeofenceList
                    geofences={geofences}
                    selectedGeofence={selectedGeofence}
                    onSelect={setSelectedGeofence}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteGeofence}
                />
            </div>

            {/* Toggle Sidebar Button (Overlay on Map) */}
            <div style={{ position: 'absolute', top: 10, left: sidebarCollapsed ? 10 : 360, zIndex: 1000, transition: 'left 0.3s' }}>
                <button
                    className="btn btn-secondary"
                    style={{ padding: '8px', borderRadius: '50%', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                >
                    <span className="material-icons-outlined">
                        {sidebarCollapsed ? 'chevron_right' : 'chevron_left'}
                    </span>
                </button>
            </div>

            {/* Map Area */}
            <div className="splits-map-container">
                <GeofenceMap
                    geofences={geofences}
                    selectedGeofence={selectedGeofence}
                    onGeofenceClick={(gf) => {
                        setSelectedGeofence(gf);
                        setIsEditModalOpen(true);
                    }}
                    onMapClick={handleMapClick}
                    onGeofenceCreate={handleGeofenceCreate}
                />
            </div>

            {/* Modals */}
            {isEditModalOpen && (
                <GeofenceModal
                    geofence={selectedGeofence}
                    onClose={() => setIsEditModalOpen(false)}
                    onSave={handleSaveGeofence}
                />
            )}

            {isImportModalOpen && (
                <ImportGeofenceModal
                    onClose={() => setIsImportModalOpen(false)}
                    onImport={handleImportStage}
                />
            )}
        </div>
    );
};

export default AdminGeofences;
